import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { OverflowRow } from '../shared/OverflowRow';
import {
  PRIORITY_ORDER, PRIORITY_LABEL, PRIORITY_COLOR, PRIORITY_HELP,
  PRICE_ORDER, PRICE_LABEL, PRICE_COLOR, PRICE_HELP,
  getCustomBuckets, customBucketColor,
  type PriorityKey, type PriceBucketKey, type CustomFilterGroup,
} from './brokerViewTokens';
import { USER_STATUS_OPTIONS, USER_STATUS_DISPLAY_DEFAULT } from '../../types/sift';

interface ChipGroupProps<K extends string> {
  options: readonly K[];
  counts: Record<K, number>;
  selected: K[];
  onToggle: (key: K) => void;
  labelMap: Record<K, string>;
  colorMap: Record<K, string | { fg: string }>;
  helpMap?: Record<K, string>;
  /** Hide the per-chip count badge (e.g. the Buyer Match group has no counts). */
  showCount?: boolean;
}

/** Render the chips for one group (no wrapping row / label). */
function ChipGroup<K extends string>({
  options, counts, selected, onToggle, labelMap, colorMap, helpMap, showCount = true,
}: ChipGroupProps<K>) {
  return (
    <>
      {options.map((key) => {
        const count = counts[key] ?? 0;
        const isActive = selected.includes(key);
        const color = colorMap[key];
        const dotColor = typeof color === 'string' ? color : color.fg;
        return (
          <button
            key={key}
            type="button"
            className={`bv-chip ${isActive ? 'active' : ''}`}
            style={{ color: dotColor }}
            onClick={() => onToggle(key)}
            aria-pressed={isActive}
            title={helpMap?.[key]}
          >
            <span className="bv-chip-dot" />
            <span style={{ color: 'var(--color-text-primary, #222)' }}>{labelMap[key]}</span>
            {showCount && <span className="bv-chip-count">{count}</span>}
          </button>
        );
      })}
    </>
  );
}

interface ChipRowProps<K extends string> extends ChipGroupProps<K> {
  label: string;
  rowHelp?: string;
}

function ChipRow<K extends string>({ label, rowHelp, ...group }: ChipRowProps<K>) {
  const { options, counts, selected, onToggle, labelMap, colorMap, helpMap, showCount = true } = group;
  const items = options.map((key) => {
    const count = counts[key] ?? 0;
    const isActive = selected.includes(key);
    const color = colorMap[key];
    const dotColor = typeof color === 'string' ? color : color.fg;
    return {
      key,
      active: isActive,
      node: (
        <button
          type="button"
          className={`bv-chip ${isActive ? 'active' : ''}`}
          style={{ color: dotColor }}
          onClick={() => onToggle(key)}
          aria-pressed={isActive}
          title={helpMap?.[key]}
        >
          <span className="bv-chip-dot" />
          <span style={{ color: 'var(--color-text-primary, #222)' }}>{labelMap[key]}</span>
          {showCount && <span className="bv-chip-count">{count}</span>}
        </button>
      ),
    };
  });

  return (
    <OverflowRow
      className="bv-chip-row"
      label={(
        <span className="bv-chip-row-label" title={rowHelp}>
          {label}
          {rowHelp && <span className="bv-chip-help" role="img" aria-label={rowHelp} title={rowHelp}> ⓘ</span>}
        </span>
      )}
      items={items}
    />
  );
}

export interface PriorityChipRowProps {
  counts: Record<PriorityKey, number>;
  selected: PriorityKey[];
  onToggle: (key: PriorityKey) => void;
}

export const PriorityChipRow: React.FC<PriorityChipRowProps> = ({ counts, selected, onToggle }) => (
  <ChipRow
    label="Urgency"
    rowHelp="Filter the board by urgency tier (computed from aging + engagement + price position). Click a tier to show only those VINs; hover a chip for what it means."
    options={PRIORITY_ORDER}
    counts={counts}
    selected={selected}
    onToggle={onToggle}
    labelMap={PRIORITY_LABEL}
    colorMap={PRIORITY_COLOR}
    helpMap={PRIORITY_HELP}
  />
);

export interface FiltersChipRowProps {
  daysCounts: number[];
  daysSelected: number[];
  onToggleDays: (index: number) => void;

  listingCounts: number[];
  listingSelected: number[];
  onToggleListing: (index: number) => void;

  bidsCounts: number[];
  bidsSelected: number[];
  onToggleBids: (index: number) => void;

  repriceCounts: number[];
  repriceSelected: number[];
  onToggleReprice: (index: number) => void;

  vdpCounts: number[];
  vdpSelected: number[];
  onToggleVdp: (index: number) => void;

  workbookCounts: number[];
  workbookSelected: number[];
  onToggleWorkbook: (index: number) => void;

  priceCounts: Record<PriceBucketKey, number>;
  priceSelected: PriceBucketKey[];
  onTogglePrice: (key: PriceBucketKey) => void;

  // Optional Buyer-Match tightness group (no counts) — "VIN has a buyer at this tier".
  matchOptions?: { label: string; value: string }[];
  matchSelected?: string[];
  onToggleMatch?: (value: string) => void;

  // Optional adverse-condition / announcement group (§8.4) — a single filter over
  // structured flags + announcement keywords. `conditionMode` sets the direction
  // (exclude selected / include only selected).
  conditionSelected?: string[];
  onToggleCondition?: (value: string) => void;
  conditionMode?: 'exclude' | 'include';
  onSetConditionMode?: (mode: 'exclude' | 'include') => void;

  // Optional observed-engagement group (§14.2) — selecting a token KEEPS only VINs
  // with real buyers who engaged THIS exact VIN (bidders / any engagement).
  engagedSelected?: string[];
  onToggleEngaged?: (value: string) => void;

  // Optional VMA market-signal INCLUSION groups (§14.5) — selecting a level KEEPS only
  // VINs whose wholesale supply / demand is at that level.
  supplySelected?: string[];
  onToggleSupply?: (value: string) => void;
  demandSelected?: string[];
  onToggleDemand?: (value: string) => void;
  // Inspection provenance (MMI / third-party / any / none). Selecting a token
  // KEEPS only VINs matching it.
  inspectionSelected?: string[];
  onToggleInspection?: (value: string) => void;

  // Optional §10-D broker VIN-status INCLUSION group — selecting a status KEEPS only
  // VINs a broker marked that way (plus `Unassigned` for un-worked VINs).
  statusSelected?: string[];
  onToggleStatus?: (value: string) => void;
}

/**
 * The vehicle-level filter groups (Days Listed, Listing Occurrences, Total Bids,
 * Price Position). Rendered as compact per-group dropdowns on a single "Filters"
 * bar so they no longer take a full wrapping row of chips. Each dropdown button
 * shows the group name and an active-count badge; opening it reveals the group's
 * chips (same OR-within / AND-across semantics as before).
 */
function FilterDropdown<K extends string>({
  label, groupHelp, ...group
}: ChipGroupProps<K> & { label: string; groupHelp?: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const activeCount = group.selected.length;

  useDropdownDismiss(open, ref, useCallback(() => setOpen(false), []));

  return (
    <div className="bv-filterdd" ref={ref}>
      <button
        type="button"
        className={`bv-filterdd-btn${activeCount > 0 ? ' active' : ''}${open ? ' open' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="true"
        title={groupHelp}
      >
        <span className="bv-filterdd-label">{label}</span>
        {activeCount > 0 && <span className="bv-filterdd-badge">{activeCount}</span>}
        <span className="bv-filterdd-caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <DropdownPop anchorRef={ref} label={label}>
          <ChipGroup {...group} />
        </DropdownPop>
      )}
    </div>
  );
}

/** Human-readable range for a custom bucket tooltip. */
function describeBucketRange(min: number | null, max: number | null): string {
  if (min != null && max != null) return min === max ? `exactly ${min}` : `${min}–${max}`;
  if (min != null) return `${min}+`;
  if (max != null) return `≤ ${max}`;
  return 'any value';
}

/**
 * A filter dropdown for a fully custom bucket group (Days Listed / Listings /
 * Bids). Buckets are admin-defined and index-based; the marker payload carries
 * the matching index per VIN, so selection + counts are keyed by index.
 */
/** The bucket chips for a custom group, shared by the dropdown and the flat
 *  overflow section so the two can never drift apart. */
function BucketChips({
  group, counts, selected, onToggle,
}: { group: CustomFilterGroup; counts: number[]; selected: number[]; onToggle: (i: number) => void }) {
  const buckets = getCustomBuckets(group);
  if (buckets.length === 0) return <div className="bv-filterdd-empty">No buckets defined</div>;
  return (
    <>
      {buckets.map((b, i) => {
        const isActive = selected.includes(i);
        return (
          <button
            key={i}
            type="button"
            className={`bv-chip ${isActive ? 'active' : ''}`}
            style={{ color: customBucketColor(i) }}
            onClick={() => onToggle(i)}
            aria-pressed={isActive}
            title={describeBucketRange(b.min, b.max)}
          >
            <span className="bv-chip-dot" />
            <span style={{ color: 'var(--color-text-primary, #222)' }}>{b.label}</span>
            <span className="bv-chip-count">{counts[i] ?? 0}</span>
          </button>
        );
      })}
    </>
  );
}

/** One flat, labelled section for the "+N" overflow menu. */
function OverflowSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <div className="bv-ovr-section-label">{label}</div>
      <div className="bv-ovr-section-chips">{children}</div>
    </>
  );
}

/**
 * The dropdown body, portalled to <body> and positioned against its button.
 *
 * These dropdowns live inside an OverflowRow, which sets `overflow: hidden` to
 * keep the filter bar on one line. That clips absolutely-positioned descendants
 * no matter their z-index, so an in-flow popover simply never appeared. Fixed
 * positioning in a portal escapes the clip; `place` keeps it on the button while
 * the page scrolls or resizes.
 */
function DropdownPop({
  anchorRef, label, children,
}: { anchorRef: React.RefObject<HTMLDivElement | null>; label: string; children: React.ReactNode }) {
  const popRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const place = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const a = anchor.getBoundingClientRect();
    const w = popRef.current?.offsetWidth ?? 0;
    // Keep the popover on screen when its button sits near the right edge.
    const left = Math.max(8, Math.min(a.left, window.innerWidth - w - 8));
    setPos({ top: a.bottom + 4, left });
  }, [anchorRef]);

  useLayoutEffect(place, [place]);

  useEffect(() => {
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [place]);

  return createPortal(
    <div
      ref={popRef}
      className="bv-filterdd-pop bv-filterdd-pop--portal"
      role="group"
      aria-label={label}
      style={{ top: pos?.top ?? -9999, left: pos?.left ?? -9999 }}
    >
      {children}
    </div>,
    document.body,
  );
}

/** Close-on-outside-click that treats the portalled popover as "inside". */
function useDropdownDismiss(
  open: boolean,
  ref: React.RefObject<HTMLDivElement | null>,
  close: () => void,
) {
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (ref.current && t && ref.current.contains(t)) return;
      // The popover is portalled out of `ref`, so a click inside it would
      // otherwise read as "outside" and close the menu before the chip fires.
      if (t?.closest?.('.bv-filterdd-pop')) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, ref, close]);
}

function CustomBucketDropdown({
  label, groupHelp, group, counts, selected, onToggle,
}: { label: string; groupHelp?: string; group: CustomFilterGroup; counts: number[]; selected: number[]; onToggle: (i: number) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const activeCount = selected.length;

  useDropdownDismiss(open, ref, useCallback(() => setOpen(false), []));

  return (
    <div className="bv-filterdd" ref={ref}>
      <button
        type="button"
        className={`bv-filterdd-btn${activeCount > 0 ? ' active' : ''}${open ? ' open' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="true"
        title={groupHelp}
      >
        <span className="bv-filterdd-label">{label}</span>
        {activeCount > 0 && <span className="bv-filterdd-badge">{activeCount}</span>}
        <span className="bv-filterdd-caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <DropdownPop anchorRef={ref} label={label}>
          <BucketChips group={group} counts={counts} selected={selected} onToggle={onToggle} />
        </DropdownPop>
      )}
    </div>
  );
}

// §8.4 unified Condition tokens: STRUCTURED flags (salvage / frame / as_is /
// second_chance) + announcement-text keyword categories. Must match the backend
// allow-list in broker_board_filter_conds.
// The whole group runs in one direction (exclude/include) set by the mode toggle.
const CONDITION_ORDER = [
  'salvage', 'frame', 'as_is', 'second_chance',
  'structural', 'flood', 'airbag', 'lemon', 'odometer', 'mechanical', 'theft', 'noarb',
];
const CONDITION_LABEL: Record<string, string> = {
  salvage: 'Salvage', frame: 'Frame / Structural', as_is: 'As-Is', second_chance: '2nd-Chance',
  structural: 'Structural (announced)',
  flood: 'Flood / Water', airbag: 'Airbag / Deployed', lemon: 'Branded Title',
  odometer: 'Odometer / TMU', mechanical: 'Mechanical / Inop', theft: 'Theft Recovery',
  noarb: 'No Arbitration',
};
const CONDITION_COLOR: Record<string, string> = {
  salvage: '#b91c1c', frame: '#b91c1c', as_is: '#9a3412', second_chance: '#7c3aed',
  structural: '#b91c1c',
  flood: '#0369a1', airbag: '#b45309', lemon: '#a16207', odometer: '#7c3aed',
  mechanical: '#9a3412', theft: '#be123c', noarb: '#9a3412',
};
const CONDITION_HELP: Record<string, string> = {
  salvage: 'Salvage-title VINs (structured flag)',
  frame: 'Frame / structural-damage VINs (structured flag)',
  as_is: 'As-is VINs (structured flag)',
  second_chance: '2nd-chance relist VINs (a digital relist of a prior unsold live/simulcast listing)',
  structural: 'Announcement declares structural / unibody DAMAGE the frame flag missed. Negations ("no structural damage") and modifications ("structural alteration — tow hitch") are not counted.',
  flood: 'Announcement mentions flood / water damage',
  airbag: 'Announcement mentions airbag / deployed',
  lemon: 'Announcement carries a title branding — lemon / manufacturer buyback / rebuilt / reconstructed',
  odometer: 'Announcement mentions odometer / TMU / not-actual mileage',
  mechanical: 'Announcement mentions inoperable / no-start / mechanical defect, an engine or transmission issue, a check-engine light, or a fluid leak. Clean-unit phrasing ("no check engine light", "no leaks") and blanket dealer policy text are not counted.',
  theft: 'Announcement mentions a recovered-theft vehicle',
  noarb: 'Announcement waives arbitration ("no arb" / "no arbitration") — a sale term, not a defect, so the buyer has no recourse.',
};

// §14.2 observed-engagement INCLUSION tokens (must match the backend allow-list in
// get_slice_vins: bidders / engaged). Selecting one KEEPS only VINs where real
// buyers engaged THIS exact VIN (vs cohort activity).
const ENGAGED_ORDER = ['bidders', 'engaged'];
const ENGAGED_LABEL: Record<string, string> = {
  bidders: 'Has bidders', engaged: 'Any engagement',
};
const ENGAGED_COLOR: Record<string, string> = {
  bidders: '#047857', engaged: '#0e7490',
};
const ENGAGED_HELP: Record<string, string> = {
  bidders: 'Only VINs a real buyer actually bid on (this exact VIN)',
  engaged: 'Only VINs a real buyer bid on, viewed, or saved (this exact VIN)',
};

// §14.5 VMA market-signal INCLUSION tokens (must match the backend allow-list in
// broker_board_filter_conds: supply low/medium/high/excessive, demand cold/warm/hot).
// Selecting a level KEEPS only VINs whose wholesale supply / demand is at that level.
const SUPPLY_ORDER = ['low', 'medium', 'high', 'excessive'];
const SUPPLY_LABEL: Record<string, string> = {
  low: 'Low', medium: 'Medium', high: 'High', excessive: 'Excessive',
};
const SUPPLY_COLOR: Record<string, string> = {
  low: '#047857', medium: '#0e7490', high: '#b45309', excessive: '#b91c1c',
};
const SUPPLY_HELP: Record<string, string> = {
  low: 'Only VINs where wholesale supply is Low (tight market — a selling advantage)',
  medium: 'Only VINs where wholesale supply is Medium',
  high: 'Only VINs where wholesale supply is High (crowded market)',
  excessive: 'Only VINs where wholesale supply is Excessive (oversupplied)',
};
const DEMAND_ORDER = ['hot', 'warm', 'cold'];
const DEMAND_LABEL: Record<string, string> = {
  hot: 'Hot', warm: 'Warm', cold: 'Cold',
};
const DEMAND_COLOR: Record<string, string> = {
  hot: '#b91c1c', warm: '#b45309', cold: '#0e7490',
};
const DEMAND_HELP: Record<string, string> = {
  hot: 'Only VINs where wholesale demand is Hot (buyers stretch — sell into strength)',
  warm: 'Only VINs where wholesale demand is Warm',
  cold: 'Only VINs where wholesale demand is Cold (soft demand)',
};

// Inspection INCLUSION tokens (must match the backend allow-list in
// broker_board_filter_conds). NOTE: this is inspection TYPE, not inspection
// MMI is inspection SOURCE = CAFS (vs ECR). The token names are legacy: `mmi` and
// `third_party` were built on inspection_type, which is inverted against source,
// so both chips used to select the opposite population. Tokens kept so saved
// views keep working; only the predicate and the labels changed.
//
// `inspected` / `not_inspected` were dropped as chips: broker_action_filter
// already requires inspect_status='Inspected', so on this board they selected
// 100% and 0% respectively. The backend still honours both tokens so existing
// saved views resolve exactly as before.
const INSPECTION_ORDER = ['mmi', 'third_party'];
const INSPECTION_LABEL: Record<string, string> = {
  mmi: 'MMI (CAFS)', third_party: 'ECR',
};
const INSPECTION_COLOR: Record<string, string> = {
  mmi: '#047857', third_party: '#0e7490',
};
const INSPECTION_HELP: Record<string, string> = {
  mmi: 'Manheim Marketplace Inspect (source CAFS). Carries a CR grade on 97.6% of units.',
  third_party: 'Electronic Condition Report (source ECR). Never carries a CR grade.',
};

// §10-D broker VIN-status INCLUSION tokens. Values are the exact status labels the
// backend stores (plus the special `Unassigned` = no broker status). Selecting a
// status KEEPS only VINs a broker marked that way; `Unassigned` keeps un-worked VINs.
const STATUS_ORDER: string[] = [...(USER_STATUS_OPTIONS as string[]), USER_STATUS_DISPLAY_DEFAULT];
const STATUS_LABEL: Record<string, string> = Object.fromEntries(STATUS_ORDER.map((s) => [s, s]));
const STATUS_COLOR: Record<string, string> = {
  'In Progress': '#1d4ed8',
  'Done': '#047857',
  'Sold / NA': '#6d28d9',
  'Updated': '#b45309',
  'Reinspection Needed': '#b91c1c',
  'Delete': '#4b5563',
  'CR Reuse': '#0f766e',
  'For Auction': '#a16207',
  'Client Elect, No List': '#7c2d12',
  [USER_STATUS_DISPLAY_DEFAULT]: '#64748b',
};
const STATUS_HELP: Record<string, string> = {
  'In Progress': 'Only VINs you marked In Progress',
  'Done': 'Only VINs you marked Done',
  'Sold / NA': 'Only VINs you marked Sold / NA',
  'Updated': 'Only VINs you marked Updated',
  'Reinspection Needed': 'Only VINs you marked Reinspection Needed',
  'Delete': 'VINs marked Delete. These are hidden from every board and count — select this to review or undo them.',
  'CR Reuse': 'Only VINs you marked CR Reuse',
  'For Auction': 'Only VINs you marked For Auction',
  'Client Elect, No List': 'Only VINs you marked Client Elect, No List',
  [USER_STATUS_DISPLAY_DEFAULT]: 'Only VINs with no broker status yet (un-worked units)',
};

/**
 * The §8.4 unified Condition dropdown: one filter over structured flags +
 * announcement keywords, with an Exclude/Include mode toggle in the popover
 * header so a broker can HIDE problem inventory (default) or narrow to it.
 */
function ConditionDropdown({
  selected, onToggle, mode, onSetMode,
}: {
  selected: string[];
  onToggle: (v: string) => void;
  mode: 'exclude' | 'include';
  onSetMode: (m: 'exclude' | 'include') => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const activeCount = selected.length;

  useDropdownDismiss(open, ref, useCallback(() => setOpen(false), []));

  const label = mode === 'include' ? 'Include' : 'Exclude';
  return (
    <div className="bv-filterdd" ref={ref}>
      <button
        type="button"
        className={`bv-filterdd-btn${activeCount > 0 ? ' active' : ''}${open ? ' open' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="true"
        title="Filter by adverse condition — structured flags (salvage / frame / as-is / 2nd-chance) and announcement keywords (flood / airbag / lemon / odometer / mechanical). Toggle Exclude (hide) vs Include (show only)."
      >
        <span className="bv-filterdd-label">Condition</span>
        {activeCount > 0 && <span className="bv-filterdd-badge">{label[0]}{activeCount}</span>}
        <span className="bv-filterdd-caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <DropdownPop anchorRef={ref} label="Condition filter">
          <div className="bv-cond-mode" role="radiogroup" aria-label="Condition direction">
            <button
              type="button"
              role="radio"
              aria-checked={mode === 'exclude'}
              className={`bv-cond-mode-btn${mode === 'exclude' ? ' active' : ''}`}
              onClick={() => onSetMode('exclude')}
              title="Hide VINs carrying any selected condition (don't reach out before verifying)."
            >Exclude</button>
            <button
              type="button"
              role="radio"
              aria-checked={mode === 'include'}
              className={`bv-cond-mode-btn${mode === 'include' ? ' active' : ''}`}
              onClick={() => onSetMode('include')}
              title="Show only VINs carrying at least one selected condition (e.g. a salvage-buyer campaign)."
            >Include only</button>
          </div>
          <ChipGroup
            options={CONDITION_ORDER}
            counts={{}}
            selected={selected}
            onToggle={onToggle}
            labelMap={CONDITION_LABEL}
            colorMap={CONDITION_COLOR}
            helpMap={CONDITION_HELP}
            showCount={false}
          />
        </DropdownPop>
      )}
    </div>
  );
}

export const FiltersChipRow: React.FC<FiltersChipRowProps> = ({
  daysCounts, daysSelected, onToggleDays,
  listingCounts, listingSelected, onToggleListing,
  bidsCounts, bidsSelected, onToggleBids,
  repriceCounts, repriceSelected, onToggleReprice,
  vdpCounts, vdpSelected, onToggleVdp,
  workbookCounts, workbookSelected, onToggleWorkbook,
  priceCounts, priceSelected, onTogglePrice,
  matchOptions, matchSelected, onToggleMatch,
  conditionSelected, onToggleCondition, conditionMode, onSetConditionMode,
  engagedSelected, onToggleEngaged,
  supplySelected, onToggleSupply,
  demandSelected, onToggleDemand,
  inspectionSelected, onToggleInspection,
  statusSelected, onToggleStatus,
}) => {
  // One entry per dropdown so OverflowRow can collapse the tail into "+N". `active`
  // drives the badge, so an applied filter is still visible once collapsed.
  const items: { key: string; active?: boolean; node: React.ReactNode; menuNode?: React.ReactNode }[] = [
    {
      key: 'days_listed',
      active: daysSelected.length > 0,
      node: (
        <CustomBucketDropdown
          label="Days Listed"
          groupHelp="How long each VIN has been listed (custom buckets)."
          group="days_listed"
          counts={daysCounts}
          selected={daysSelected}
          onToggle={onToggleDays}
        />
      ),
      menuNode: (
        <OverflowSection label="Days Listed">
          <BucketChips group="days_listed" counts={daysCounts} selected={daysSelected} onToggle={onToggleDays} />
        </OverflowSection>
      ),
    },
    {
      key: 'listing_occ',
      active: listingSelected.length > 0,
      node: (
        <CustomBucketDropdown
          label="Listings"
          groupHelp="How many times each VIN has been listed (custom buckets)."
          group="listing_occ"
          counts={listingCounts}
          selected={listingSelected}
          onToggle={onToggleListing}
        />
      ),
      menuNode: (
        <OverflowSection label="Listings">
          <BucketChips group="listing_occ" counts={listingCounts} selected={listingSelected} onToggle={onToggleListing} />
        </OverflowSection>
      ),
    },
    {
      key: 'total_bids',
      active: bidsSelected.length > 0,
      node: (
        <CustomBucketDropdown
          label="Bids"
          groupHelp="Number of bids each VIN has received (custom buckets)."
          group="total_bids"
          counts={bidsCounts}
          selected={bidsSelected}
          onToggle={onToggleBids}
        />
      ),
      menuNode: (
        <OverflowSection label="Bids">
          <BucketChips group="total_bids" counts={bidsCounts} selected={bidsSelected} onToggle={onToggleBids} />
        </OverflowSection>
      ),
    },
    {
      key: 'reprice',
      active: repriceSelected.length > 0,
      node: (
        <CustomBucketDropdown
          label="Repriced"
          groupHelp="How recently each VIN was repriced — days since the last price change (custom buckets). Never-repriced VINs match no bucket."
          group="reprice"
          counts={repriceCounts}
          selected={repriceSelected}
          onToggle={onToggleReprice}
        />
      ),
      menuNode: (
        <OverflowSection label="Repriced">
          <BucketChips group="reprice" counts={repriceCounts} selected={repriceSelected} onToggle={onToggleReprice} />
        </OverflowSection>
      ),
    },
    {
      key: 'vdp_views',
      active: vdpSelected.length > 0,
      node: (
        <CustomBucketDropdown
          label="VDP Views"
          groupHelp="Total VDP (vehicle detail page) views on the listing — a demand-interest signal (custom buckets)."
          group="vdp_views"
          counts={vdpCounts}
          selected={vdpSelected}
          onToggle={onToggleVdp}
        />
      ),
      menuNode: (
        <OverflowSection label="VDP Views">
          <BucketChips group="vdp_views" counts={vdpCounts} selected={vdpSelected} onToggle={onToggleVdp} />
        </OverflowSection>
      ),
    },
    {
      key: 'workbook_saves',
      active: workbookSelected.length > 0,
      node: (
        <CustomBucketDropdown
          label="Saves"
          groupHelp="How many times the listing was saved to a buyer workbook — a strong intent signal (custom buckets)."
          group="workbook_saves"
          counts={workbookCounts}
          selected={workbookSelected}
          onToggle={onToggleWorkbook}
        />
      ),
      menuNode: (
        <OverflowSection label="Saves">
          <BucketChips group="workbook_saves" counts={workbookCounts} selected={workbookSelected} onToggle={onToggleWorkbook} />
        </OverflowSection>
      ),
    },
    {
      key: 'price',
      active: priceSelected.length > 0,
      node: (
        <FilterDropdown
          label="Price"
          groupHelp="Asking price vs market (MMR) position."
          options={PRICE_ORDER}
          counts={priceCounts}
          selected={priceSelected}
          onToggle={onTogglePrice}
          labelMap={PRICE_LABEL}
          colorMap={PRICE_COLOR}
          helpMap={PRICE_HELP}
        />
      ),
      menuNode: (
        <OverflowSection label="Price">
          <ChipGroup
            options={PRICE_ORDER}
            counts={priceCounts}
            selected={priceSelected}
            onToggle={onTogglePrice}
            labelMap={PRICE_LABEL}
            colorMap={PRICE_COLOR}
            helpMap={PRICE_HELP}
          />
        </OverflowSection>
      ),
    },
  ];

  if (matchOptions && matchSelected && onToggleMatch) {
    items.push({
      key: 'match',
      active: matchSelected.length > 0,
      node: (
        <FilterDropdown<string>
          label="Buyer Match"
          groupHelp="Show VINs that have a matched buyer at this tightness (best-available on Seller/Cluster; this buyer on the Buyer tab)."
          options={matchOptions.map((o) => o.value)}
          counts={{}}
          selected={matchSelected}
          onToggle={onToggleMatch}
          labelMap={Object.fromEntries(matchOptions.map((o) => [o.value, o.label]))}
          colorMap={Object.fromEntries(matchOptions.map((o) => [o.value, '#1d4ed8']))}
          showCount={false}
        />
      ),
      menuNode: (
        <OverflowSection label="Buyer Match">
          <ChipGroup<string>
            options={matchOptions.map((o) => o.value)}
            counts={{}}
            selected={matchSelected}
            onToggle={onToggleMatch}
            labelMap={Object.fromEntries(matchOptions.map((o) => [o.value, o.label]))}
            colorMap={Object.fromEntries(matchOptions.map((o) => [o.value, '#1d4ed8']))}
            showCount={false}
          />
        </OverflowSection>
      ),
    });
  }
  if (conditionSelected && onToggleCondition) {
    items.push({
      key: 'condition',
      active: conditionSelected.length > 0,
      node: (
        <ConditionDropdown
          selected={conditionSelected}
          onToggle={onToggleCondition}
          mode={conditionMode ?? 'exclude'}
          onSetMode={onSetConditionMode ?? (() => {})}
        />
      ),
      menuNode: (
        <OverflowSection label="Condition">
          <div className="bv-cond-mode" role="radiogroup" aria-label="Condition direction">
            <button
              type="button"
              role="radio"
              aria-checked={(conditionMode ?? 'exclude') === 'exclude'}
              className={`bv-cond-mode-btn${(conditionMode ?? 'exclude') === 'exclude' ? ' active' : ''}`}
              onClick={() => (onSetConditionMode ?? (() => {}))('exclude')}
            >Exclude</button>
            <button
              type="button"
              role="radio"
              aria-checked={conditionMode === 'include'}
              className={`bv-cond-mode-btn${conditionMode === 'include' ? ' active' : ''}`}
              onClick={() => (onSetConditionMode ?? (() => {}))('include')}
            >Include only</button>
          </div>
          <ChipGroup<string>
            options={CONDITION_ORDER}
            counts={{}}
            selected={conditionSelected}
            onToggle={onToggleCondition}
            labelMap={CONDITION_LABEL}
            colorMap={CONDITION_COLOR}
            helpMap={CONDITION_HELP}
            showCount={false}
          />
        </OverflowSection>
      ),
    });
  }
  if (engagedSelected && onToggleEngaged) {
    items.push({
      key: 'engaged',
      active: engagedSelected.length > 0,
      node: (
        <FilterDropdown<string>
          label="Engaged"
          groupHelp="Keep only VINs where a real buyer engaged THIS exact VIN — bid on it, or viewed/saved it (observed engagement, not cohort activity). Narrows the VIN work tables."
          options={ENGAGED_ORDER}
          counts={{}}
          selected={engagedSelected}
          onToggle={onToggleEngaged}
          labelMap={ENGAGED_LABEL}
          colorMap={ENGAGED_COLOR}
          helpMap={ENGAGED_HELP}
          showCount={false}
        />
      ),
      menuNode: (
        <OverflowSection label="Engaged">
          <ChipGroup<string>
            options={ENGAGED_ORDER}
            counts={{}}
            selected={engagedSelected}
            onToggle={onToggleEngaged}
            labelMap={ENGAGED_LABEL}
            colorMap={ENGAGED_COLOR}
            helpMap={ENGAGED_HELP}
            showCount={false}
          />
        </OverflowSection>
      ),
    });
  }
  if (supplySelected && onToggleSupply) {
    items.push({
      key: 'supply',
      active: supplySelected.length > 0,
      node: (
        <FilterDropdown<string>
          label="Supply"
          groupHelp="Keep only VINs at these wholesale supply levels (VMA market data). Low supply = a tighter, seller-favorable market; VINs with no market data are hidden while this is active."
          options={SUPPLY_ORDER}
          counts={{}}
          selected={supplySelected}
          onToggle={onToggleSupply}
          labelMap={SUPPLY_LABEL}
          colorMap={SUPPLY_COLOR}
          helpMap={SUPPLY_HELP}
          showCount={false}
        />
      ),
      menuNode: (
        <OverflowSection label="Supply">
          <ChipGroup<string>
            options={SUPPLY_ORDER}
            counts={{}}
            selected={supplySelected}
            onToggle={onToggleSupply}
            labelMap={SUPPLY_LABEL}
            colorMap={SUPPLY_COLOR}
            helpMap={SUPPLY_HELP}
            showCount={false}
          />
        </OverflowSection>
      ),
    });
  }
  if (demandSelected && onToggleDemand) {
    items.push({
      key: 'demand',
      active: demandSelected.length > 0,
      node: (
        <FilterDropdown<string>
          label="Demand"
          groupHelp="Keep only VINs at these wholesale demand levels (VMA market data). Hot demand = buyers stretch on price; VINs with no market data are hidden while this is active."
          options={DEMAND_ORDER}
          counts={{}}
          selected={demandSelected}
          onToggle={onToggleDemand}
          labelMap={DEMAND_LABEL}
          colorMap={DEMAND_COLOR}
          helpMap={DEMAND_HELP}
          showCount={false}
        />
      ),
      menuNode: (
        <OverflowSection label="Demand">
          <ChipGroup<string>
            options={DEMAND_ORDER}
            counts={{}}
            selected={demandSelected}
            onToggle={onToggleDemand}
            labelMap={DEMAND_LABEL}
            colorMap={DEMAND_COLOR}
            helpMap={DEMAND_HELP}
            showCount={false}
          />
        </OverflowSection>
      ),
    });
  }
  if (inspectionSelected && onToggleInspection) {
    items.push({
      key: 'inspection',
      active: inspectionSelected.length > 0,
      node: (
        <FilterDropdown<string>
          label="Inspection"
          groupHelp="Keep only VINs with this inspection provenance. This is inspection TYPE — true MMI (inspection source CAFS vs ECR) needs a VIN_DETAILS change."
          options={INSPECTION_ORDER}
          counts={{}}
          selected={inspectionSelected}
          onToggle={onToggleInspection}
          labelMap={INSPECTION_LABEL}
          colorMap={INSPECTION_COLOR}
          helpMap={INSPECTION_HELP}
          showCount={false}
        />
      ),
      menuNode: (
        <OverflowSection label="Inspection">
          <ChipGroup<string>
            options={INSPECTION_ORDER}
            counts={{}}
            selected={inspectionSelected}
            onToggle={onToggleInspection}
            labelMap={INSPECTION_LABEL}
            colorMap={INSPECTION_COLOR}
            helpMap={INSPECTION_HELP}
            showCount={false}
          />
        </OverflowSection>
      ),
    });
  }
  if (statusSelected && onToggleStatus) {
    items.push({
      key: 'status',
      active: statusSelected.length > 0,
      node: (
        <FilterDropdown<string>
          label="Status"
          groupHelp="Keep only VINs with these broker statuses (your VIN status). 'Unassigned' keeps un-worked VINs. Applies to every board view."
          options={STATUS_ORDER}
          counts={{}}
          selected={statusSelected}
          onToggle={onToggleStatus}
          labelMap={STATUS_LABEL}
          colorMap={STATUS_COLOR}
          helpMap={STATUS_HELP}
          showCount={false}
        />
      ),
      menuNode: (
        <OverflowSection label="Status">
          <ChipGroup<string>
            options={STATUS_ORDER}
            counts={{}}
            selected={statusSelected}
            onToggle={onToggleStatus}
            labelMap={STATUS_LABEL}
            colorMap={STATUS_COLOR}
            helpMap={STATUS_HELP}
            showCount={false}
          />
        </OverflowSection>
      ),
    });
  }

  return (
    <OverflowRow
      className="bv-chip-row bv-filter-bar"
      label={(
        <span
          className="bv-chip-row-label"
          title="Narrow the board by aging + engagement + pricing signals (the inputs behind urgency). OR within a group, AND across groups."
        >
          Filters
          <span
            className="bv-chip-help"
            role="img"
            aria-label="Filter by aging, engagement, and pricing signals"
            title="Narrow the board by aging + engagement + pricing signals (the inputs behind urgency). OR within a group, AND across groups."
          > ⓘ</span>
        </span>
      )}
      items={items}
    />
  );
};
